import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Loader, Menu, Table, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useBoardAdminList, useOwnerCandidates, useSetOwner } from '@/pages/Boards/hooks'
import { TrashTab } from './TrashTab'

type AdminBoards = NonNullable<ReturnType<typeof useBoardAdminList>['data']>['boards']
type AdminBoard = AdminBoards[number]

// Per-row owner assign/change control: a Menu listing the board's org
// practitioners (fetched lazily, only once opened) plus an Unassign action.
// Menu (not Popover+Select) — Select-in-Popover swallowed clicks as
// outside-clicks before onChange could commit. Errors surface via
// useSetOwner's own notification.
const OwnerPicker = ({ board }: { board: AdminBoard }) => {
  const [opened, setOpened] = useState(false)
  const { data, isLoading } = useOwnerCandidates(opened ? board.id : undefined)
  const setOwner = useSetOwner()
  const candidates = data?.candidates ?? []

  const choose = (ownerRef: string | null) => {
    void setOwner.mutateAsync({ id: board.id, ownerRef }).catch(() => undefined)
  }

  return (
    <Menu opened={opened} onChange={setOpened} position="bottom-end" shadow="md">
      <Menu.Target>
        {/* No onClick here: the Menu is controlled via opened/onChange, and a
            second toggle on the target fights it — the two updates net `opened`
            back to false, disabling the candidates query (gated on `opened`) even
            while the dropdown is visible. Let Mantine's onChange be the sole driver. */}
        <Button size="xs" variant="default" loading={setOwner.isPending}>
          {board.owner ? 'Change' : 'Assign'}
        </Button>
      </Menu.Target>
      <Menu.Dropdown className="min-w-60">
        {isLoading ? (
          <div className="flex justify-center py-2">
            <Loader size="xs" />
          </div>
        ) : candidates.length === 0 ? (
          <Menu.Item disabled>No practitioners in this org</Menu.Item>
        ) : (
          candidates.map((c) => (
            <Menu.Item
              key={c.reference}
              leftSection={
                c.reference === board.owner?.reference ? <IconCheck size={14} /> : undefined
              }
              onClick={() => choose(c.reference)}
            >
              {c.display}
            </Menu.Item>
          ))
        )}
        {board.owner && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={() => choose(null)}>
              Unassign
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

export const BoardAdminTab = () => {
  const [, setSearchParams] = useSearchParams()
  const { data, isLoading } = useBoardAdminList(true)
  const boards = data?.boards ?? []

  const openBoard = (id: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', 'care-queue')
      next.set('board', id)
      return next
    })

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-medium">Boards</h2>
          <Text size="sm" c="dimmed">
            Care queue boards in your organization. Assign or change the owning practitioner.
          </Text>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader size="sm" />
          </div>
        ) : boards.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border border-[#EEEEEE] bg-white py-16">
            <p className="text-sm text-gray-500">No boards yet</p>
          </div>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Board</Table.Th>
                <Table.Th>Org</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {boards.map((b) => (
                <Table.Tr key={b.id}>
                  <Table.Td>
                    <button
                      type="button"
                      onClick={() => openBoard(b.id)}
                      className="hover:text-primary cursor-pointer text-left text-sm transition"
                    >
                      {b.name}
                    </button>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={(b.orgName ?? b.orgId) ? undefined : 'dimmed'}>
                      {b.orgName ?? b.orgId ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={b.owner ? undefined : 'dimmed'}>
                      {b.owner?.display ?? '— Unassigned'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <div className="flex justify-end">
                      <OwnerPicker board={b} />
                    </div>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-medium">Trash</h2>
        <TrashTab />
      </div>
    </div>
  )
}
